// useExperimentDateHelper.ts
import { useMemo } from 'react';
import { Experiment, Workspace } from '../../../types';

/**
 * useExperimentDateHelper
 * 
 * A custom React hook that formats experiment date properties according to workspace timezone.
 * All dates are converted to the specified timezone and returned in multiple format options.
 * 
 * @param {Experiment} experiment - The experiment object containing date properties (optional)
 * @param {Workspace} workspace - The workspace object containing timezone info (optional)
 * @returns {ExperimentDates} Object containing formatted dates for all experiment date fields
 * 
 * @example
 * // Basic usage - single experiment
 * const ExperimentDetail = ({ experiment, workspace }) => {
 *   const { createdAt, updatedAt, startedAt, endedAt, lastViewedAt, today, dateRange } = 
 *     useExperimentDateHelper(experiment, workspace);
 * 
 *   return (
 *     <div>
 *       <p>{createdAt?.fullWithTimezone}</p>  // "Jan 1, 2023, 12:18 PM CST"
 *       <p>{updatedAt?.full}</p>               // "Nov 1, 2024, 3:00 PM"
 *       <p>{startedAt?.dateOnly}</p>           // "Jan 1, 2023"
 *       <p>{endedAt?.timeOnly}</p>             // "12:18 PM"
 *       <p>{lastViewedAt?.ago}</p>             // "26 days ago"
 *       <p>{createdAt?.duration}</p>           // "26 days"
 *       <p>{startedAt?.monthDay}</p>           // "Sep 26"
 *       <p>{today?.dateOnly}</p>               // Current date: "Oct 23, 2025"
 *       // dangerouslySetInnerHTML={{ __html: dateRange?.startToToday || ''
 *       // Renders: Sep 25 - Oct 22 (<span class="text-gray text-xs ml-1">26 days</span>)
 *       // {dateRange?.startToTodayPlain}</p>  // "Sep 25 - Oct 22 (26 days)"
 *   );
 * };
 * 
 * @example
 * // Compare if a date is today
 * const { createdAt, today } = useExperimentDateHelper(experiment, workspace);
 * const isCreatedToday = createdAt?.dateOnly === today?.dateOnly;
 * 
 * @example
 * // Safe usage with optional chaining
 * const { createdAt } = useExperimentDateHelper(experiment, workspace);
 * {createdAt?.fullWithTimezone && <p>Created: {createdAt.fullWithTimezone}</p>}
 * 
 * @example
 * // Handle undefined experiment/workspace
 * const { createdAt, today } = useExperimentDateHelper(undefined, workspace);
 * // Returns: { createdAt: null, updatedAt: null, ..., today: null }
 * 
 * Available date formats for each date field:
 * - fullWithTimezone: "Jan 1, 2023, 12:18 PM CST"
 * - full: "Nov 1, 2024, 3:00 PM"
 * - dateOnly: "Jan 1, 2023"
 * - monthDay: "Sep 26"
 * - timeOnly: "12:18 PM"
 * - ago: "26 days ago"
 * - duration: "26 days" (without "ago")
 * - iso: "2025-10-21T15:23:31.524Z" (original ISO string)
 * - date: Date object (converted to timezone)
 * - timestamp: Unix timestamp (number)
 * - isValid: boolean (checks if date is valid)
 * 
 * Additional properties:
 * - today: FormattedDate (current date/time in workspace timezone)
 * - dateRange: { startToToday, startToEnd, createdToToday } (formatted date ranges with HTML-wrapped duration)
 *   - HTML version: 'Sep 25 (<span class="text-gray text-xs ml-1">1 day</span>)'
 *   - Plain version: 'Sep 25 (1 day)' (use properties with 'Plain' suffix)
 */

interface FormattedDate {
  iso: string;
  date: Date;
  dateKey: string;
  fullWithTimezone: string;
  full: string;
  dateOnly: string;
  monthDay: string;
  timeOnly: string;
  ago: string;
  duration: string;
  timestamp: number;
  isValid: boolean;
}

interface DateRange {
  startToToday: string | null;       // HTML: "Sep 25 - Oct 22 (<span>26 days</span>)"
  startToTodayPlain: string | null;  // Plain: "Sep 25 - Oct 22 (26 days)"
  startToEnd: string | null;         // HTML: "Sep 25 - Oct 22 (<span>26 days</span>)"
  startToEndPlain: string | null;    // Plain: "Sep 25 - Oct 22 (26 days)"
  createdToToday: string | null;     // HTML: "Sep 25 - Oct 22 (<span>26 days</span>)"
  createdToTodayPlain: string | null; // Plain: "Sep 25 - Oct 22 (26 days)"
}

interface ExperimentDates {
  updatedAt: FormattedDate | null;
  createdAt: FormattedDate | null;
  startedAt: FormattedDate | null;
  publishedAt: FormattedDate | null;
  endedAt: FormattedDate | null;
  lastViewedAt: FormattedDate | null;
  today: FormattedDate | null;
  dateRange: DateRange;
}

export const useExperimentDateHelper = (
  experiment?: Experiment | null,
  workspace?: Workspace | null
): ExperimentDates => {
  return useMemo(() => {
    // Return empty object if experiment or workspace is undefined/null
    if (!experiment || !workspace) {
      return {
        updatedAt: null,
        createdAt: null,
        startedAt: null,
        publishedAt: null,
        endedAt: null,
        lastViewedAt: null,
        today: null,
        dateRange: {
          startToToday: null,
          startToTodayPlain: null,
          startToEnd: null,
          startToEndPlain: null,
          createdToToday: null,
          createdToTodayPlain: null
        }
      };
    }

    const timezone = workspace?.timezone || 'UTC';

    const formatDate = (dateString: string | null): FormattedDate | null => {
      if (!dateString) return null;

      const date = new Date(dateString);
      const timezoneAbbr = getTimezoneAbbreviation(date, timezone);
      const dateInTimezone = new Date(date.toLocaleString('en-US', { timeZone: timezone }));

      const dateParts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(date);
      const yearPart = dateParts.find((part) => part.type === 'year')?.value ?? '1970';
      const monthPart = dateParts.find((part) => part.type === 'month')?.value ?? '01';
      const dayPart = dateParts.find((part) => part.type === 'day')?.value ?? '01';
      const dateKey = `${yearPart}-${monthPart}-${dayPart}`;

      // Manually construct formats for consistency across browsers
      const month = date.toLocaleDateString('en-US', {
        timeZone: timezone,
        month: 'short'
      });
      const day = date.toLocaleDateString('en-US', {
        timeZone: timezone,
        day: 'numeric'
      });
      const year = date.toLocaleDateString('en-US', {
        timeZone: timezone,
        year: 'numeric'
      });
      const time = date.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      // Format: "Jan 1, 2023, 12:18 PM CST" (manually constructed to avoid "at")
      const fullWithTimezone = `${month} ${day}, ${year}, ${time} ${timezoneAbbr}`;

      // Format: "Nov 1, 2024, 3:00 PM" (manually constructed to avoid "at")
      const full = `${month} ${day}, ${year} ${time}`;

      const dateOnly = `${month} ${day}, ${year}`;

      // Format: "Sep 26"
      const monthDay = `${month} ${day}`;

      const timeOnly = time;

      const relativeData = getRelativeTime(dateInTimezone, timezone);

      return {
        iso: dateString,
        date: dateInTimezone,
        dateKey,
        fullWithTimezone,
        full,
        dateOnly,
        monthDay,
        timeOnly,
        ago: relativeData.ago,
        duration: relativeData.duration,
        timestamp: date.getTime(),
        isValid: !isNaN(date.getTime())
      };
    };

    // Get current date in the workspace timezone
    const now = new Date();
    const todayFormatted = formatDate(now.toISOString());

    const formattedDates = {
      updatedAt: formatDate(experiment.updated_at),
      createdAt: formatDate(experiment.created_at),
      startedAt: formatDate(experiment.started_at),
      publishedAt: formatDate(experiment.published_at ?? null),
      endedAt: formatDate(experiment.ended_at),
      lastViewedAt: formatDate(experiment.last_viewed_at),
      today: todayFormatted
    };

    // Helper function to create date range string
    const createDateRange = (
      startDate: FormattedDate | null,
      endDate: FormattedDate | null
    ): { html: string | null; plain: string | null } => {
      if (!startDate || !endDate) return { html: null, plain: null };

      // Count inclusive calendar days in workspace timezone, independent of time-of-day.
      const start = new Date(`${startDate.dateKey}T00:00:00Z`);
      const end = new Date(`${endDate.dateKey}T00:00:00Z`);
      const diffInDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      const durationText = diffInDays <= 1 ? '1 day' : `${diffInDays} days`;
      const durationHtml = `<span class="text-body-muted text-xs">(${durationText})</span>`;

      // If 1 day or less, show single date format
      if (diffInDays <= 1) {
        return {
          html: `${startDate.monthDay}${'&nbsp;'}${durationHtml}`,
          plain: `${startDate.monthDay}${' '}(${durationText})`
        };
      }

      // Otherwise show range format
      return {
        html: `${startDate.monthDay} - ${endDate.monthDay}${'&nbsp;'}${durationHtml}`,
        plain: `${startDate.monthDay} - ${endDate.monthDay}${' '}(${durationText})`
      };
    };

    // Build date ranges
    const startToToday = createDateRange(formattedDates.startedAt, formattedDates.today);
    const startToEnd = createDateRange(formattedDates.startedAt, formattedDates.endedAt);
    const createdToToday = createDateRange(formattedDates.createdAt, formattedDates.today);

    const dateRange: DateRange = {
      startToToday: startToToday.html,
      startToTodayPlain: startToToday.plain,
      startToEnd: startToEnd.html,
      startToEndPlain: startToEnd.plain,
      createdToToday: createdToToday.html,
      createdToTodayPlain: createdToToday.plain
    };

    return {
      ...formattedDates,
      dateRange
    };
  }, [
    experiment?.updated_at,
    experiment?.created_at,
    experiment?.started_at,
    experiment?.published_at,
    experiment?.ended_at,
    experiment?.last_viewed_at,
    workspace?.timezone
  ]);
};

function getTimezoneAbbreviation(date: Date, timezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    return timeZonePart ? timeZonePart.value : '';
  } catch (error) {
    return '';
  }
}

interface RelativeTime {
  ago: string;
  duration: string;
}

function getRelativeTime(date: Date, timezone: string): RelativeTime {
  const now = new Date();
  const nowInTimezone = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
  const diffInSeconds = Math.floor((nowInTimezone.getTime() - date.getTime()) / 1000);

  let duration = '';

  if (diffInSeconds < 60) {
    duration = 'just now';
    return { ago: duration, duration: '0 seconds' };
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    duration = `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'}`;
    return { ago: duration + ' ago', duration };
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    duration = `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'}`;
    return { ago: duration + ' ago', duration };
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    duration = `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'}`;
    return { ago: duration + ' ago', duration };
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    duration = `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'}`;
    return { ago: duration + ' ago', duration };
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  duration = `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'}`;
  return { ago: duration + ' ago', duration };
}

export default useExperimentDateHelper;
